/**
 * Both catalogues are bundled into the JS and the locale is read before the
 * first render, so a language switch is one state change and there is no
 * flicker (plan 0019, Language). The catalogues are the old app's, ICU format.
 */
import IntlMessageFormat from 'intl-messageformat';
import { create } from 'zustand';
import en from './en.json';
import nb from './nb.json';

export type Locale = 'nb' | 'en' | 'pl' | 'lt';
export const LOCALES: [Locale, string][] = [
  ['nb', 'Norsk bokmål'],
  ['en', 'English'],
  ['pl', 'Polski'],
  ['lt', 'Lietuvių'],
];
const INTL: Record<Locale, string> = { nb: 'nb-NO', en: 'en', pl: 'pl', lt: 'lt' };
type Catalogue = Record<string, Record<string, string>>;
// the old catalogue exists in nb and en only; pl and lt read the English lines for those keys
const CATS: Record<Locale, Catalogue> = {
  nb: nb as Catalogue,
  en: en as Catalogue,
  pl: en as Catalogue,
  lt: en as Catalogue,
};

function readLocale(): Locale {
  try {
    const v = localStorage.getItem('timefly.locale');
    if (v === 'en' || v === 'nb' || v === 'pl' || v === 'lt') return v;
  } catch {
    /* ignore */
  }
  return 'nb';
}

interface I18nState {
  locale: Locale;
  setLocale: (l: Locale) => void;
}
export const useI18n = create<I18nState>((set) => ({
  locale: readLocale(),
  setLocale: (locale) => {
    try {
      localStorage.setItem('timefly.locale', locale);
    } catch {
      /* ignore */
    }
    document.documentElement.lang = locale;
    set({ locale });
  },
}));

const cache = new Map<string, IntlMessageFormat>();

/** `t('nav.timeline')`, or `t('timeline.rule_break', { hours: 8 })`. Falls back to English, then to the key. */
export function translate(locale: Locale, key: string, values?: Record<string, string | number>): string {
  const [ns, ...rest] = key.split('.');
  const k = rest.join('.');
  const msg = CATS[locale][ns ?? '']?.[k] ?? CATS.en[ns ?? '']?.[k];
  if (msg == null) return key;
  if (!values) return msg;
  const ck = `${locale}:${key}`;
  let f = cache.get(ck);
  if (!f) {
    f = new IntlMessageFormat(msg, locale === 'nb' ? 'nb-NO' : 'en');
    cache.set(ck, f);
  }
  return String(f.format(values));
}

/**
 * The new screens' own strings, Norwegian source text as the key (web.nb.json is
 * the identity). A line Polish or Lithuanian has not got yet reads the English one,
 * as the old catalogue does; an unknown key returns the text itself, so a missing
 * English line shows Norwegian rather than a key. The Shell remounts the page tree on a
 * locale change, so a plain function is enough here.
 */
import webEn from './web.en.json';
import webLt from './web.lt.json';
import webPl from './web.pl.json';
const WEB: Record<Locale, Record<string, string>> = {
  nb: {},
  en: webEn as Record<string, string>,
  pl: webPl as Record<string, string>,
  lt: webLt as Record<string, string>,
};
export function T(text: string, values?: Record<string, string | number>): string {
  const locale = useI18n.getState().locale;
  const msg = WEB[locale][text] ?? (locale === 'nb' ? undefined : WEB.en[text]) ?? text;
  if (!values) return msg;
  const ck = `web:${locale}:${text}`;
  let f = cache.get(ck);
  if (!f) {
    f = new IntlMessageFormat(msg, INTL[locale]);
    cache.set(ck, f);
  }
  return String(f.format(values));
}

export function useT(): (key: string, values?: Record<string, string | number>) => string {
  const locale = useI18n((s) => s.locale);
  return (key, values) => translate(locale, key, values);
}
